import AxiosClient from "../../../Network/AxiosClient";
import { env } from "../../../utilities/config";
import { buildGetApiUrl } from "../../../utilities/helpers";
import {
  getAssignmentFieldName,
  getSubmittedUserType,
} from "../utils/userHelper";
const baseUrl = env.BASE_URL;
const USER_LIST_API_PATH = "/api/users/api/v1/get-users";
const USER_DETAILS_API_PATH = "/api/users/api/v1/details";
const ROLE_HISTORY_API_PATH = "/api/users/api/v1/role-history";
const USER_LOG_API_PATH = "/api/users/api/v1/user-log";
const UNREGISTER_FACEMATCHING_API_PATH =
  "/api/users/api/v1/unregister-facematching";
const USER_CREATE_API_PATH = "/api/users/api/v1/user";
const USER_UPDATE_API_PATH = "/api/users/api/v1/user";
const USER_ACTIVATE_DEACTIVATE_API_PATH = "/api/users/api/v1/activate";

const ASSIGNMENT_FIELDS = [
  "cluster",
  "region",
  "area",
  "territory",
  "agency",
  "warehouse",
];

const PHONE_FIELDS = ["contact_number", "personal_number"];

const normalizePhoneValue = (value) => {
  if (typeof value !== "string") {
    return value;
  }

  if (value.startsWith("+88")) {
    const strippedValue = value.substring(3);
    return strippedValue.trim() ? strippedValue : "";
  }

  return value.trim();
};

const isEmptyFieldValue = (key, value) => {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === "string") {
    if (PHONE_FIELDS.includes(key)) {
      return normalizePhoneValue(value) === "";
    }

    return value.trim() === "";
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
};

const buildVerificationPayload = ({
  verification,
  verification_type,
  verification_id,
}) => {
  const sanitizedVerificationType =
    typeof verification_type === "string" ? verification_type.trim() : "";
  const sanitizedVerificationId =
    typeof verification_id === "string" ? verification_id.trim() : "";

  if (sanitizedVerificationType && sanitizedVerificationId) {
    return [
      {
        type: sanitizedVerificationType,
        value: sanitizedVerificationId,
      },
    ];
  }

  if (verification_type && verification_id) {
    return [
      {
        type: verification_type,
        value: verification_id,
      },
    ];
  }

  if (Array.isArray(verification) && verification.length > 0) {
    return verification.filter(
      (item) =>
        item &&
        typeof item === "object" &&
        typeof item.type === "string" &&
        item.type.trim() &&
        typeof item.value === "string" &&
        item.value.trim()
    );
  }

  if (
    verification &&
    typeof verification === "object" &&
    typeof verification.type === "string" &&
    verification.type.trim() &&
    typeof verification.value === "string" &&
    verification.value.trim()
  ) {
    return [verification];
  }

  return [];
};

const sanitizeAssignmentPayload = (assignments = []) => {
  return assignments.filter(
    (item) =>
      item &&
      ((typeof item === "object" &&
        item.id !== "" &&
        item.id !== null &&
        item.id !== undefined &&
        item.value !== "") ||
        (typeof item === "string" && item.trim() !== ""))
  );
};

export const buildGetUsersUrl = ({
  pageNo = 1,
  pageSize = 10,
  userType = "",
  query = "",
  status = "",
} = {}) => {
  const url = buildGetApiUrl(USER_LIST_API_PATH, {
    page_no: pageNo,
    page_size: pageSize,
    user_type: userType,
    query,
    status,
  });

  return url;
};

export const userFetcher = async (url) => {
  const response = await AxiosClient.get(url);
  return response.data;
};

export const userDetailsFetcher = async (userId) => {
  const url = buildGetApiUrl(USER_DETAILS_API_PATH, {
    id: userId,
  });

  const response = await AxiosClient.get(url);

  return response.data?.data || response.data;
};

export const userService = {
  buildFormData: (formData) => {
    const data = new FormData();
    const normalizedUserType = getSubmittedUserType(
      formData.user_type,
      formData.support_level
    );
    const assignmentField = getAssignmentFieldName(normalizedUserType);

    const {
      confirm_password,
      verification,
      verification_type,
      verification_id,
      user_type,
      image,
      ...restFormData
    } = formData;

    const normalizedFormData = {
      ...restFormData,
      user_type: normalizedUserType,
    };

    const verificationPayload = buildVerificationPayload({
      verification,
      verification_type,
      verification_id,
    });

    if (verificationPayload.length > 0) {
      data.append("verification", JSON.stringify(verificationPayload));
    }

    Object.keys(normalizedFormData).forEach((key) => {
      if (key === "image" && normalizedFormData[key]) {
        data.append("image", normalizedFormData[key]);
      } else if (ASSIGNMENT_FIELDS.includes(key)) {
        const assignmentValue = normalizedFormData[key];
        const sanitizedAssignments = Array.isArray(assignmentValue)
          ? sanitizeAssignmentPayload(assignmentValue)
          : [];

        if (key === assignmentField && sanitizedAssignments.length > 0) {
          data.append(key, JSON.stringify(sanitizedAssignments));
        }
      } else if (!isEmptyFieldValue(key, normalizedFormData[key])) {
        let valueToSend = normalizedFormData[key];

        if (PHONE_FIELDS.includes(key)) {
          valueToSend = normalizePhoneValue(valueToSend);
        }

        data.append(key, valueToSend);
      }
    });

    if (formData.image instanceof Blob) {
      data.append("image", formData.image);
    }

    return data;
  },

  getUsers: async ({
    query = "",
    pageNo = 1,
    pageSize = 10,
    userType = "",
    status = "",
  } = {}) => {
    const url = buildGetUsersUrl({
      pageNo,
      pageSize,
      userType,
      query,
      status,
    });

    return userFetcher(url);
  },

  createUser: async (userData) => {
    try {
      const url = new URL(USER_CREATE_API_PATH, baseUrl);
      const response = await AxiosClient.post(url.toString(), userData);
      return response.data;
    } catch (error) {
      console.error("Error creating user:", error);
      throw error;
    }
  },

  updateUser: async (userData) => {
    try {
      const url = new URL(USER_UPDATE_API_PATH, baseUrl);
      const response = await AxiosClient.put(url.toString(), userData);
      return response.data;
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  },

  getRoleHistory: async (userId, year) => {
    try {
      const url = buildGetApiUrl(ROLE_HISTORY_API_PATH, {
        year: year,
        user_id: userId,
      });

      const response = await AxiosClient.get(url);
      return response.data;
    } catch (error) {
      console.error("Error fetching role history:", error);
      throw error;
    }
  },

  getUserLogs: async (userId, pageNo = 0) => {
    try {
      const url = buildGetApiUrl(USER_LOG_API_PATH, {
        page_no: pageNo,
        user_id: userId,
      });

      const response = await AxiosClient.get(url);
      return response.data;
    } catch (error) {
      console.error("Error fetching user logs:", error);
      throw error;
    }
  },

  unregisterFacematching: async (employeeId) => {
    try {
      const url = new URL(UNREGISTER_FACEMATCHING_API_PATH, baseUrl);
      const response = await AxiosClient.post(url.toString(), {
        employee_id: employeeId,
      });

      return response.data;
    } catch (error) {
      console.error("Error unregistering facematching:", error);
      throw error;
    }
  },

  activateDeactivateUser: async ({
    userId,
    command,
    resigningDate = null,
  } = {}) => {
    try {
      const normalizedResigningDate =
        command !== "deactivate" ||
        resigningDate === undefined ||
        resigningDate === null ||
        (typeof resigningDate === "string" && resigningDate.trim() === "")
          ? null
          : resigningDate;

      const payload = {
        user_id: userId,
        resigning_date: normalizedResigningDate,
        command,
      };

      const url = new URL(USER_ACTIVATE_DEACTIVATE_API_PATH, baseUrl);
      const response = await AxiosClient.post(url.toString(), payload);

      return response.data;
    } catch (error) {
      console.error("Error updating user status:", error);
      throw error;
    }
  },
};
